// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2015
//
// ### multipleNoteSolfegeID.ts
// ##  Identify several notes in scale degrees or solfege
//
import type { ModeAlias } from '../uTheory.Notation/scale';
import Scale from '../uTheory.Notation/scale';
import type { UserData } from './interactivePrototype';
import type { OneNoteSolfegeIDOptions } from './oneNoteSolfegeID';
import { compareDegrees } from './oneNoteSolfegeID';
import type { SolfegeQuestionObject } from '../uTheory.Skills/solfegeQuestionGenerator';
import SolfegeQuestionGenerator from '../uTheory.Skills/solfegeQuestionGenerator';
import * as Font from '../uTheory.Font/font';
import SolfegeSystem from '../uTheory.Notation/solfegeSystem';
import type { AnswerAndData } from './interactiveQuestionPrototype';
import { MultipleNoteSolfegeDictationInteractivePrototype } from './sharedSolfegeIdMethods';
import type { InteractiveRegistryEntry } from '../uTheory.Skills/interactiveRegistry';
import type { ScaleDegree } from './solfegeDefinitions';

export interface MultipleNoteSolfegeIDOptions extends OneNoteSolfegeIDOptions {
  drill?: true;
  questions?: string[];
  repetition: number;
  /** Default 4 notes per question */
  notesPerQuestion?: number;
}

export default class MultipleNoteSolfegeID extends MultipleNoteSolfegeDictationInteractivePrototype {
  declare questions: SolfegeQuestionObject[];

  declare currentQuestion: SolfegeQuestionObject;

  declare options: MultipleNoteSolfegeIDOptions;

  protected answerDiv: HTMLDivElement;

  constructor(displayDiv: HTMLDivElement, options: MultipleNoteSolfegeIDOptions, userData?: any) {
    // copy options so they don't get rewritten on themselves...
    options = { ...options };
    super(displayDiv, options, userData);
    this.setSolfegeSystem();
    this.targetSpeedFromSkills = MultipleNoteSolfegeID.registry[0].skills;
    this.init(displayDiv, options, userData);
  }

  init(displayDiv: HTMLDivElement, options: MultipleNoteSolfegeIDOptions, userData?: UserData) {
    options.maxQuestions = options.maxQuestions || 8;
    this.generator = new SolfegeQuestionGenerator();

    if (typeof options.level !== 'number') {
      // We need a level for showing the right buttons, and we should
      // do everything in our power to figure it out if it's not given.
      if (options.levelFromUserData) {
        options.level = this.getLevel(userData, 'multiple');
      } else if (options.skills && !options.hasOwnProperty('level')) {
        options.level = this.setLevelFromSkill(options);
      } else if (options.hasOwnProperty('level') && !options.skills) {
        options.skills = [this.getSkillFromLevel(options)];
      } else if (userData && options.drill) {
        options.level = this.getLevel(userData, 'multiple');
      } else {
        throw new Error(
          'options.skills or options.level must be provided in manual mode to configure which buttons are visible.'
        );
      }
    }

    // Set default notesPerQuestion:
    if (options.drill && !options.notesPerQuestion) {
      options.notesPerQuestion = 4;
    }

    this.settings = {
      loadAudioFirst: true,
      instructionsPanel: {
        innerHTML:
          '<p>You will hear a chord progression to establish ' +
          ' the key, followed by several notes in that key.</p>' +
          '<p>Click the solfege/scale degrees of the notes you hear.</p>',
        callback: this.startGame,
        thisArg: this,
      },
      instructions: true,
      editor: false,
      notation: false,
      piano: false,
      letters: {
        solfege: { system: this.system, quality: 'Major' },
        customButtons: [
          {
            text: 'Play T<u>o</u>nic',
            value: 'playTonic',
            hotKey: ['o', 'O'],
            variable: 'playTonic',
          },
          { tagName: 'br', perspective: ['tall', 'portrait', 'square'] },
          { text: 'Play <u>N</u>otes', value: 'play', hotKey: ['n', 'N'], variable: 'playNotes' },
          { tagName: 'br' },
        ],
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    if (
      options.testMode &&
      typeof this.settings.letters === 'object' &&
      Array.isArray(this.settings.letters.customButtons)
    ) {
      this.settings.letters.customButtons.push({
        text: Font.toHTML('symbol', 'trash'),
        value: 'delete',
        hotKey: ['backspace', 'del', 'ctrl+z', 'command+z'],
      });
      this.settings.letters.customButtons.push({
        text: 'Check',
        value: 'check',
        hotKey: ['enter'],
      });
      this.settings.letters.customButtons.push({
        tagName: 'br',
      });
    }

    return super.init(displayDiv, options, userData);
  }

  createCustomDivs() {
    const options = { fontSize: '2vw', textAlign: 'center' };
    const div = this.createDiv('answerDiv', options, false);
    div.innerHTML = 'Do Re Mi Fa Sol';
    this.displayDiv.insertBefore(div, this.lettersDiv);

    this.layout.answerDiv = {
      div,
      name: 'answerDiv',
      percent: 15,
      visible: true,
      callback: this.sizeTextDiv.bind(this),
    };
  }

  /**
   * Only available in testMode, deletes the last inputted scale degree
   */
  protected deleteAnswer() {
    if (!this.options.testMode) throw Error('Non-testMode should not be able to call deleteAnswer');
    if (this.answerStack.length < 1) return;
    this.answerStack.pop();
    this.updateAnswerDiv();
  }

  addListeners() {
    function buttonHandler(input) {
      switch (input) {
        case 'play':
          this.play();
          break;
        case 'playTonic':
          this.playTonic();
          break;
        case 'delete':
          this.deleteAnswer();
          break;
        case 'check':
          this.checkpointAnswer();
          break;
        default:
          this.checkAnswer(input);
      }
    }

    this.letterPanel.addCustomButtonCallback(buttonHandler, this);
  }

  startGame() {
    this.gameStarted = true;
    this.displayInstructions();
    this.letterPanel.solfege.setSolfegeButtons(this.key.quality, this.levelData.degrees);
    this.resize();
  }

  displayInstructions(disableAudio = false) {
    super.displayInstructions(disableAudio);
    this.updateAnswerDiv();
  }

  updateAnswerDiv() {
    const mode = this.key.quality;
    this.answerDiv.setAttribute('aria-live', this.answerStack.length ? 'polite' : 'off');
    const answerDegrees = this.levelData.firstNoteGiven
      ? [this.currentQuestion.degrees[0], ...this.answerStack.map((a) => a.answer)]
      : this.answerStack.map((a) => a.answer);
    this.answerDiv.innerHTML = this.currentQuestion.degrees.reduce(
      (prior, current, index): string => {
        if (index !== 0) prior += ' ';
        if (index < answerDegrees.length) {
          const answerText = `${SolfegeSystem.degreeToSolfege(
            answerDegrees[index],
            mode,
            this.system
          )}`;
          prior += answerText;
        } else {
          prior += '___';
        }
        return prior as string;
      },
      ''
    ) as string;
  }

  checkAnswer(answer: ScaleDegree) {
    if (this.rightAnswerDelay) return;
    const degree = this.currentQuestion.degrees[this.questionPartCount + this.firstNoteGivenOffset];
    const correct = compareDegrees(degree, answer);

    if (this.options.testMode) {
      if (this.answerStack.length < this.currentQuestion.degrees.length) {
        this.questionPartCount++;
        this.answerStack.push({
          answer,
          correct,
          correctAnswer: degree,
        });
        this.updateAnswerDiv();
      }
      return;
    }

    if (correct) {
      let delay = 500;
      if (this.questions[this.questionNumber + 1] && this.questions[this.questionNumber + 1].key) {
        delay = 1000;
      }
      this.answerStack.push({ answer });
      this.rightAnswer(delay);
    } else {
      this.wrongAnswer();
    }
  }

  getScaleAndFirstWrongDegree(): { degree: string; scale: Scale } {
    const keyQuality = this.key.quality;
    let quality: string = keyQuality;
    let degree = this.currentQuestion.degrees[this.questionPartCount + this.firstNoteGivenOffset];
    if (degree === 'M6') {
      quality = 'melodic minor';
      degree = '6';
    }
    if (degree === 'M7') {
      quality = 'harmonic minor';
      degree = '7';
    }

    const scale = new Scale(this.key.tonic, quality as ModeAlias);
    return { scale, degree };
  }

  rightAnswer(delay?: number) {
    this.wrongAnswerCount = 0;
    ++this.questionPartCount;
    this.updateAnswerDiv();

    // Only show check when all notes are listed:
    if (
      this.questionPartCount + this.firstNoteGivenOffset ===
      this.currentQuestion.degrees.length
    ) {
      this.questionPartCount = 0;
      super.rightAnswer(delay);
    } else {
      this.progressTracker.rightAnswer(this.currentQuestion.skills, false);
    }
  }

  gameOver() {
    this.updateInstructions('Nice work!');
    this.answerDiv.innerHTML = '';
    super.gameOver();
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(): AnswerAndData {
    const answerStack = this.currentQuestion.degrees.map((degree, i) => {
      const hasAnswer = !!this.answerStack[i];
      const answerObj = this.answerStack[i];
      const text = hasAnswer
        ? this.solfegeSystem.degreeToSolfege(answerObj.answer, this.key.quality)
        : '__';
      const answer = hasAnswer ? text : '__';
      return {
        ...(this.answerStack[i] || {}),
        text,
        answer,
        correct: hasAnswer ? answerObj.correct : false,
        correctAnswer: degree,
      };
    });

    const rightAnswers = answerStack.reduce((prior, q) => prior + Number(q.correct) * 1, 0);
    const wrongAnswers = answerStack.length - rightAnswers;

    const question = this.currentQuestion.degrees.reduce((prior, deg) => {
      const degreeInSystem = `${this.solfegeSystem.degreeToSolfege(
        deg,
        this.currentQuestion.key.quality
      )}`;
      return prior + (prior ? ' ' : '') + degreeInSystem;
    }, '');

    const studentAnswerText = answerStack.reduce(
      (prior, ans) => prior + (prior ? ' ' : '') + ans.text,
      ''
    );

    const answerAndData = {
      studentAnswerText,
      rightAnswers,
      wrongAnswers,
      question: `${question}`,
      questionType: 'MultipleNoteSolfegeID',
      questionText: `Name the ${this.solfegeSystem} you hear.`,
      questionSVG: null,
      studentAnswerSVG: null,
      correctAnswerText: `${question}`,
      skills: [this.currentQuestion.skills],
    };
    return answerAndData;
  }

  static registry: InteractiveRegistryEntry[] = [
    {
      description: 'Melodic fragment solfege/scale-degree ID',
      options: {
        levelFromUserData: true,
        tonicizeEvery: 4,
      },
      skills: [
        'sd_frag_123_major',
        'sd_frag_123_minor',
        'sd_frag_12345_major',
        'sd_frag_12345_minor',
        'sd_frag_5678_major',
        'sd_frag_5678_minor',
        'sd_frag_5678_all_minors',
        'sd_frag_1_to_8_major',
        'sd_frag_1_to_8_minor',
        'sd_frag_1_to_8_major_w_octaves',
        'sd_frag_1_to_8_minor_w_octaves',
      ],
    },
    {
      description: 'Stepwise solfege/scale degree dictation (first note given)',
      options: {
        level: -4,
        notesPerQuestion: 3,
        tonicizeEvery: 20,
      },
      skills: ['sd_frag_stepwise_major'],
    },
  ];
}
